import { SanityComplexPortableText } from '@data/sanity/queries/types/content'
import {
  SanityPrintSlotSettings,
  SanityPrintSlotType,
  SanityPrintType,
} from '@data/sanity/queries/types/print'
import { SanityPrintColor } from '@data/sanity/queries/types/shop'
import {
  SanityPrintConfiguratorStrings,
  SanityShopFormStrings,
  SanitySiteStrings,
} from '@data/sanity/queries/types/strings'
import { PrintColorOptions, ShopFormStepType } from './types'

type PrintSlotTypeTextMap = Record<SanityPrintSlotType, string | undefined>

type PrintSlotTypeContentMap = Record<
  SanityPrintSlotType,
  SanityComplexPortableText | undefined
>

type PrintTypeTextMap = Record<SanityPrintType, string | undefined>

type PrintColorTextMap = Record<SanityPrintColor, string | undefined>

type StepTypeTextMap = Record<ShopFormStepType, string | undefined>

export const freetextRegex = /<a?:.+?:\d{18}>|\p{Extended_Pictographic}/gu

/**
 * Gets the label for print slot freetext input.
 */
export const getPrintFreetextInputLabel = (
  printConfiguratorStrings: SanityPrintConfiguratorStrings,
  printSlotType: SanityPrintSlotType,
) => {
  if (printSlotType === 'back') {
    return printConfiguratorStrings.printConfiguratorAlternativeFreetextInputLabel
  }

  return printConfiguratorStrings.printConfiguratorFreetextInputLabel
}

/**
 * Gets the text for print slot submit button.
 */
export const getPrintSlotSubmitButtonText = (
  strings: SanitySiteStrings,
  inStock: boolean,
  showAddToCartButton: boolean,
  isCartProductAdding: boolean,
) => {
  if (!inStock) {
    return strings.productOutOfStock
  }

  if (!showAddToCartButton) {
    return strings.buttonNext ?? ''
  }

  if (isCartProductAdding) {
    return strings.buttonAdding
  }

  return strings.buttonAddToCart
}

/**
 * Gets the title of upload section.
 */
export const getUploadSectionTitle = (
  shopFormStrings: SanityShopFormStrings,
  printSlotType: SanityPrintSlotType,
) => {
  const sectionTitleMap: PrintSlotTypeTextMap = {
    front: shopFormStrings.shopFormUploadStepFrontTitle,
    back: shopFormStrings.shopFormUploadStepBackTitle,
  }

  return sectionTitleMap[printSlotType] ?? ''
}

/**
 * Gets the description of upload section.
 */
export const getUploadSectionDescription = (
  shopFormStrings: SanityShopFormStrings,
  printSlotType: SanityPrintSlotType,
) => {
  const sectionDescriptionMap: PrintSlotTypeContentMap = {
    front: shopFormStrings.shopFormUploadStepFrontDescription,
    back: shopFormStrings.shopFormUploadStepBackDescription,
  }

  return sectionDescriptionMap[printSlotType] ?? []
}

/**
 * Gets the title of review section.
 */
export const getReviewSectionTitle = (
  shopFormStrings: SanityShopFormStrings,
  printSlotType: SanityPrintSlotType,
) => {
  const sectionTitleMap: PrintSlotTypeTextMap = {
    front: shopFormStrings.shopFormReviewStepFrontTitle,
    back: shopFormStrings.shopFormReviewStepBackTitle,
  }

  return sectionTitleMap[printSlotType] ?? ''
}

/**
 * Gets the description of review section.
 */
export const getReviewSectionDescription = (
  shopFormStrings: SanityShopFormStrings,
  printSlotType: SanityPrintSlotType,
) => {
  const sectionDescriptionMap: PrintSlotTypeContentMap = {
    front: shopFormStrings.shopFormReviewStepFrontDescription,
    back: shopFormStrings.shopFormReviewStepBackDescription,
  }

  return sectionDescriptionMap[printSlotType] ?? []
}

/**
 * Gets the step title for shop form progress indicator.
 */
export const getShopFormStepTitle = (
  shopFormStrings: SanityShopFormStrings,
  stepType: ShopFormStepType,
  printSlot?: SanityPrintSlotSettings,
) => {
  const stepTypeTextMap: StepTypeTextMap = {
    upload: printSlot?.name ?? '',
    shop: shopFormStrings.shopFormShopStepName,
    review: shopFormStrings.shopFormReviewStepName,
  }

  return stepTypeTextMap[stepType]
}

/**
 * Gets the text shown on print preview image for print slot and print type.
 */
export const getPrintPreviewText = (
  shopFormStrings: SanityShopFormStrings,
  printSlotType: SanityPrintSlotType,
  printType: SanityPrintType,
) => {
  switch (printSlotType) {
    case 'front': {
      const printTypeTextMap: PrintTypeTextMap = {
        logo: shopFormStrings.shopFormPrintPreviewFrontLogo,
        image: shopFormStrings.shopFormPrintPreviewFrontImage,
        text: shopFormStrings.shopFormPrintPreviewFrontText,
      }

      return printTypeTextMap[printType]
    }

    case 'back': {
      const printTypeTextMap: PrintTypeTextMap = {
        logo: shopFormStrings.shopFormPrintPreviewBackLogo,
        image: shopFormStrings.shopFormPrintPreviewBackImage,
        text: shopFormStrings.shopFormPrintPreviewBackText,
      }

      return printTypeTextMap[printType]
    }
  }
}

/**
 * Gets print color radio item options.
 */
export const getPrintColorOptions = (strings: SanitySiteStrings) => {
  const printColorOptions: PrintColorOptions[] = [
    {
      value: 'dark',
      label: strings.colorDark,
      dotClassName: 'border-black bg-black',
    },
    {
      value: 'light',
      label: strings.colorLight,
      dotClassName: 'border-gray-light bg-white',
    },
  ]

  return printColorOptions
}

/**
 * Gets string representation of print type.
 */
export const getPrintTypeString = (
  shopFormStrings: SanityShopFormStrings,
  printType: SanityPrintType,
) => {
  const defaultPrintTypeString = `${printType[0].toUpperCase()}${printType.slice(1)}`
  const printTypeStringMap: PrintTypeTextMap = {
    logo: shopFormStrings.shopFormLogoPrintTypeTitle,
    image: shopFormStrings.shopFormImagePrintTypeTitle,
    text: defaultPrintTypeString,
  }

  return printTypeStringMap[printType] ?? defaultPrintTypeString
}

/**
 * Gets string representation of print color.
 */
export const getPrintColorString = (
  shopFormStrings: SanityShopFormStrings,
  printColor: SanityPrintColor,
) => {
  const defaultPrintColorString = `${printColor[0].toUpperCase()}${printColor.slice(1)}`
  const printColorStringMap: PrintColorTextMap = {
    light: shopFormStrings.shopFormLightPrintColorTitle,
    dark: shopFormStrings.shopFormDarkPrintColorTitle,
  }

  return printColorStringMap[printColor] ?? defaultPrintColorString
}

/**
 * Gets optimized print title based on print type and print color.
 */
export const getOptimizedPrintTitle = (
  printTypes: SanityPrintType[],
  printColor: SanityPrintColor,
  shopFormStrings?: SanityShopFormStrings,
) => {
  if (!shopFormStrings) {
    return
  }

  const printTypesString = printTypes
    .map((printType) => getPrintTypeString(shopFormStrings, printType))
    .join(' / ')
  const printColorString = getPrintColorString(shopFormStrings, printColor)

  return shopFormStrings.shopFormOptimizedPrintTitle
    .replace(/{print_type}/gi, printTypesString)
    .replace(/{print_color}/gi, printColorString)
}

/**
 * Gets original print title based on print type.
 */
export const getOriginalPrintTitle = (
  printTypes: SanityPrintType[],
  shopFormStrings?: SanityShopFormStrings,
) => {
  if (!shopFormStrings) {
    return
  }

  const printTypesString = printTypes
    .map((printType) => getPrintTypeString(shopFormStrings, printType))
    .join(' / ')

  return shopFormStrings.shopFormOriginalPrintTitle.replace(
    /{print_type}/gi,
    printTypesString,
  )
}

/**
 * Gets tooltip text for print item.
 */
export const getPrintItemTooltipText = (
  shopFormStrings: SanityShopFormStrings,
  printColor?: SanityPrintColor,
  isOptimized?: boolean,
) => {
  if (!isOptimized) {
    return shopFormStrings.shopFormOriginalPrintTooltip
  }

  const printColorStringMap: PrintColorTextMap = {
    light: shopFormStrings.shopFormOptimizedLightPrintTooltip,
    dark: shopFormStrings.shopFormOptimizedDarkPrintTooltip,
  }

  if (!printColor) {
    return
  }

  return printColorStringMap[printColor]
}
